// src/pages/Consignacion/Tabs/TabStockMinimo.js
//
// El stock mínimo de cada artículo y su historial de cambios.
//
// Vive en SAP (MinimalStock de la ficha del almacén) y SAP no
// guarda historial de ese campo, así que el backend compara contra su propia
// copia en cada consulta y registra lo que cambió. Abrir esta solapa es lo que
// dispara esa comparación: si alguien lo tocó en SAP, se entera acá.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { formatNumber } from '../../../utils/formato';
import '../Consignacion.css';

/** "2026-09-11T16:07:13.000Z" → "11/09/2026 13:07" */
const formatFechaHora = (valor) => {
  if (!valor) return '—';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
};

/** "2026-09-11" → "11/09/2026", sin pasar por Date (que corre la zona horaria). */
const formatFecha = (iso) => {
  if (!iso) return '—';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};

const ORIGENES = {
  alta:   { texto: 'Alta',      clase: 'bg-secondary-subtle text-secondary-emphasis' },
  portal: { texto: 'Portal',    clase: 'bg-primary-subtle text-primary-emphasis' },
  sap:    { texto: 'Desde SAP', clase: 'bg-warning-subtle text-warning-emphasis' }
};

const TabStockMinimo = () => {
  const [datos, setDatos] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [soloCambiados, setSoloCambiados] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      // En paralelo: son dos consultas independientes y la tabla no sirve de
      // mucho sin el historial al lado.
      const [minimos, log] = await Promise.all([
        api.get('/consignacion/stock-minimo'),
        api.get('/consignacion/stock-minimo/historial')
      ]);
      setDatos(minimos.data);
      setHistorial(log.data.cambios || []);
    } catch (err) {
      console.error('Error cargando el stock mínimo:', err);
      setError(err.response?.data?.error || 'No se pudo cargar el stock mínimo de consignación.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const articulos = (datos?.articulos || []).filter(a => {
    // "Cambiados" son los que tienen un cambio real registrado; el alta no
    // cuenta, porque es la foto inicial y la tienen todos.
    if (soloCambiados && (!a.ultimoCambio || a.ultimoCambio.origen === 'alta')) return false;
    if (!busqueda.trim()) return true;
    const t = busqueda.trim().toLowerCase();
    return a.codigo.toLowerCase().includes(t)
        || a.descripcion.toLowerCase().includes(t)
        || (a.proveedorNombre || '').toLowerCase().includes(t);
  });

  const conCambios = (datos?.articulos || [])
    .filter(a => a.ultimoCambio && a.ultimoCambio.origen !== 'alta').length;

  if (cargando) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2">Leyendo el stock mínimo y comparando contra SAP...</p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      {error && <div className="alert alert-danger">{error}</div>}

      {datos && (
        <>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex flex-wrap gap-2">
              <span className="chip-resumen">
                <i className="bi bi-sliders"></i> {datos.articulos.length} artículos
              </span>
              <span className={`chip-resumen ${conCambios > 0 ? 'semaforo-sobrestock' : ''}`}>
                {conCambios > 0 && <span className="punto" />}
                {conCambios} con cambios registrados
              </span>
            </div>
            <button className="btn btn-outline-primary" onClick={cargar} disabled={cargando}>
              <i className="bi bi-arrow-clockwise me-1"></i> Revisar cambios
            </button>
          </div>

          {datos.cambiosDetectados > 0 && (
            <div className="alert alert-warning mt-3 mb-0 d-flex align-items-center gap-2">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>
                Se detectaron <strong>{datos.cambiosDetectados}</strong> cambio(s) de stock mín./máx. desde
                la última consulta. Quedaron registrados en el historial de abajo.
              </span>
            </div>
          )}

          {/* --- Tabla de stock mínimo y máximo --- */}
          <div className="card shadow-sm mt-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h5 className="card-title mb-0">Stock mínimo y máximo por artículo</h5>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <div className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="soloCambiados"
                      checked={soloCambiados}
                      onChange={e => setSoloCambiados(e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor="soloCambiados">
                      Sólo los que cambiaron
                    </label>
                  </div>
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    style={{ width: '240px' }}
                    placeholder="Buscar por código, artículo o proveedor..."
                    value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                  />
                </div>
              </div>

              {articulos.length === 0 ? (
                <p className="text-muted mb-0">No hay artículos que coincidan con el filtro.</p>
              ) : (
                <div className="tabla-consignacion">
                  <table className="table table-sm table-hover align-middle">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: '100px' }}>Código</th>
                        <th>Artículo</th>
                        <th>Proveedor</th>
                        <th className="text-end" style={{ width: '85px' }}>Stock mín.</th>
                        <th className="text-end" style={{ width: '85px' }}>Stock máx.</th>
                        <th className="text-end" style={{ width: '80px' }}>Stock</th>
                        <th style={{ width: '190px' }}>Último cambio</th>
                        <th style={{ width: '150px' }}>Artículo tocado en SAP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articulos.map(a => {
                        const cambio = a.ultimoCambio;
                        const esCambioReal = cambio && cambio.origen !== 'alta';
                        return (
                          <tr key={a.codigo}>
                            <td><code>{a.codigo}</code></td>
                            <td className="small celda-descripcion" title={a.descripcion}>{a.descripcion}</td>
                            <td className="small text-muted">{a.proveedorNombre}</td>
                            <td className="text-end fw-semibold">{formatNumber(a.stockMinimo)}</td>
                            <td className={`text-end ${a.stockMaximo > 0 ? 'fw-bold text-danger' : 'text-muted'}`}
                                title={a.stockMaximo > 0 ? 'Un máximo distinto de 0 vuelve a topear las recepciones del proveedor' : undefined}>
                              {formatNumber(a.stockMaximo)}
                              {a.stockMaximo > 0 && <i className="bi bi-exclamation-triangle-fill ms-1"></i>}
                            </td>
                            <td className="text-end text-muted">{formatNumber(a.stock)}</td>
                            <td className="small">
                              {esCambioReal ? (
                                <>
                                  <span className="text-nowrap">
                                    <span className="text-muted">{formatNumber(cambio.anterior)}</span>
                                    <i className="bi bi-arrow-right mx-1 text-muted"></i>
                                    <strong>{formatNumber(cambio.nuevo)}</strong>
                                  </span>
                                  <span className={`badge ms-2 fw-normal ${ORIGENES[cambio.origen]?.clase || ''}`}>
                                    {ORIGENES[cambio.origen]?.texto || cambio.origen}
                                  </span>
                                  <span className="d-block text-muted">{formatFechaHora(cambio.fecha)}</span>
                                </>
                              ) : (
                                <span className="text-muted">Sin cambios desde el alta</span>
                              )}
                            </td>
                            <td className="small text-muted text-nowrap">
                              {formatFecha(a.sapUpdateDate)}
                              {a.sapUpdateTime && <span className="ms-1">{String(a.sapUpdateTime).slice(0, 5)}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* --- Historial completo --- */}
          <div className="card shadow-sm mt-3">
            <div className="card-body">
              <h5 className="card-title mb-1">Historial de cambios</h5>
              <p className="text-muted small">
                Cada vez que se abre esta solapa se compara el stock mínimo y máximo de cada artículo contra SAP.
                Lo que cambió queda acá. Las altas no se listan: son la foto inicial, no un cambio.
              </p>

              {historial.length === 0 ? (
                <p className="text-muted mb-0">
                  Todavía no se registró ningún cambio. A partir de ahora, cualquier modificación
                  de esos valores va a aparecer en esta lista.
                </p>
              ) : (
                <div className="tabla-consignacion">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: '150px' }}>Detectado</th>
                        <th style={{ width: '100px' }}>Código</th>
                        <th className="text-end" style={{ width: '90px' }}>Antes</th>
                        <th className="text-end" style={{ width: '90px' }}>Después</th>
                        <th style={{ width: '110px' }}>Origen</th>
                        <th>Artículo tocado en SAP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historial.map((c, i) => (
                        <tr key={`${c.codigo}-${c.fecha}-${i}`}>
                          <td className="small text-nowrap">{formatFechaHora(c.fecha)}</td>
                          <td><code className="small">{c.codigo}</code></td>
                          <td className="text-end text-muted">{formatNumber(c.anterior)}</td>
                          <td className="text-end fw-semibold">{formatNumber(c.nuevo)}</td>
                          <td>
                            <span className={`badge fw-normal ${ORIGENES[c.origen]?.clase || ''}`}>
                              {ORIGENES[c.origen]?.texto || c.origen}
                            </span>
                          </td>
                          <td className="small text-muted">
                            {formatFecha(c.sapFecha)}
                            {c.sapHora && <span className="ms-1">{String(c.sapHora).slice(0, 5)}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TabStockMinimo;
